import { SITE_URL } from '../lib/siteConfig';

const MANUFACTURER = { '@type': 'Organization', name: 'Medical Inventi S.A.', url: 'https://medicalinventi.pl/' };
const DISTRIBUTOR_URL = 'https://www.fmsmedequip.com/';

const faqPage = (url, faqs) => ({
  '@type': 'FAQPage',
  '@id': `${url}#faq`,
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

const breadcrumb = (url, name) => ({
  '@type': 'BreadcrumbList',
  '@id': `${url}#breadcrumb`,
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
    { '@type': 'ListItem', position: 2, name, item: url },
  ],
});

const build = ({ path, name, image, description, fdaCode, fdaLabel, specialty, indications, faqs }) => {
  const url = `${SITE_URL}${path}`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'MedicalDevice',
        '@id': `${url}#device`,
        name,
        description,
        url,
        image: `${SITE_URL}${image}`,
        relevantSpecialty: { '@type': 'MedicalSpecialty', name: specialty },
        purpose: 'Bone defect filling and regeneration',
        indication: indications.map((i) => ({ '@type': 'MedicalIndication', name: i })),
        identifier: { '@type': 'PropertyValue', name: fdaLabel, value: fdaCode },
        manufacturer: MANUFACTURER,
        sameAs: [DISTRIBUTOR_URL, 'https://medicalinventi.pl/'],
      },
      breadcrumb(url, name),
      faqPage(url, faqs),
    ],
  };
};

export const orthoSchema = build({
  path: '/flexioss-ortho',
  name: 'FlexiOss® Ortho',
  image: '/flexioss-ortho-transparent.png',
  description:
    'FlexiOss® Ortho is a third-generation, 100% synthetic biphasic hydroxyapatite–curdlan (β-1,3-glucan) bone substitute for orthopedic and trauma surgery. After hydration it becomes flexible and moldable, can be loaded with antibiotics or PRP, and is slowly remodelled into natural bone — eliminating implant-removal reoperation.',
  fdaCode: 'CMDR-2025-04943A',
  fdaLabel: 'PH-FDA Certificate of Medical Device Registration',
  specialty: 'Orthopedic Surgery',
  indications: [
    'Comminuted fractures with bone tissue defects',
    'Long bone defect filling and reconstruction (up to 5–7 cm)',
    'Bone tumour resection defect reconstruction',
    'Spinal fusion augmentation and arthrodesis',
  ],
  faqs: [
    {
      q: 'What is FlexiOss® Ortho used for?',
      a: 'FlexiOss® Ortho fills and reconstructs bone defects in orthopedic and trauma surgery — comminuted fractures, long-bone defects up to 5–7 cm, post-infective and post-cyst cavities, tumour-resection defects, osteoporotic fracture therapy, spinal fusion augmentation, and revision/arthrodesis procedures.',
    },
    {
      q: 'Is FlexiOss® Ortho synthetic or animal-derived?',
      a: 'It is 100% synthetic — a biphasic hydroxyapatite–curdlan (β-1,3-glucan) composite with no bovine, cadaveric, or animal-derived material, which removes prion and viral transmission risk.',
    },
    {
      q: 'Can FlexiOss® Ortho be loaded with antibiotics?',
      a: 'Yes. After hydration in sterile saline or autologous blood the scaffold absorbs solutions and can be loaded intra-operatively with antibiotics (e.g. gentamicin, vancomycin), platelet-rich plasma, or growth factors for localized delivery at the surgical site.',
    },
    {
      q: 'Is FlexiOss® Ortho approved in the Philippines?',
      a: 'Yes. FlexiOss® Ortho is PH-FDA certified under CMDR-2025-04943A and holds CE certification in Europe (EC Certificate No. 1434-MDD-269/2021).',
    },
  ],
});

export const dentSchema = build({
  path: '/flexioss-dent',
  name: 'FlexiOss® Dent',
  image: '/flexioss-dent-transparent.png',
  description:
    'FlexiOss® Dent is a synthetic, biphasic hydroxyapatite–curdlan bone substitute for dental and maxillofacial surgery — socket and ridge preservation, sinus floor elevation, ridge augmentation, peri-implant and cyst-cavity filling. Moldable, animal-free, and usable membrane-free; supplied in 1 cm, 3 cm, and 5 cm formats.',
  fdaCode: 'CMDR-2025-04943B',
  fdaLabel: 'PH-FDA Certificate of Medical Device Registration',
  specialty: 'Dentistry',
  indications: [
    'Post-extraction socket and ridge preservation',
    'Sinus floor elevation (sinus lift)',
    'Alveolar ridge augmentation',
    'Peri-implant and cyst-cavity defect filling',
  ],
  faqs: [
    {
      q: 'What dental procedures is FlexiOss® Dent used for?',
      a: 'Socket and ridge preservation after extraction, sinus floor elevation, alveolar ridge augmentation, peri-implant defect filling, and cyst-cavity filling. It can be used membrane-free.',
    },
    {
      q: 'What sizes does FlexiOss® Dent come in?',
      a: 'FlexiOss® Dent is supplied in 1 cm, 3 cm, and 5 cm formats, then cut and shaped chairside to the defect after hydration.',
    },
    {
      q: 'Is FlexiOss® Dent animal-free?',
      a: 'Yes — it is a fully synthetic hydroxyapatite–curdlan composite with no bovine or porcine material, removing prion and viral transmission risk.',
    },
    {
      q: 'Is FlexiOss® Dent PH-FDA certified?',
      a: 'Yes, under CMDR-2025-04943B for dental and maxillofacial applications, with CE certification in Europe.',
    },
  ],
});

export const vetSchema = build({
  path: '/flexioss-vet',
  name: 'FlexiOss® Vet',
  image: '/flexioss-vet.png',
  description:
    'FlexiOss® Vet brings human-grade, third-generation hydroxyapatite–curdlan bone-regeneration science to veterinary surgery — for tooth-extraction bone defects, oronasal fistulae, periodontal bone loss, and angular limb-deformity correction in dogs, cats, and horses. Osteoconductive, flexible, and animal-origin-free.',
  fdaCode: 'EC 1434-MDD-269/2021',
  fdaLabel: 'CE Certificate',
  specialty: 'Veterinary Surgery',
  indications: [
    'Tooth-extraction and ectopic-tooth bone defects',
    'Oronasal fistulae and periodontal bone loss',
    'Angular limb-deformity correction',
    'Companion and equine bone reconstruction',
  ],
  faqs: [
    {
      q: 'What animals can be treated with FlexiOss® Vet?',
      a: 'Companion and equine patients — dogs, cats, and horses — for tooth-extraction bone defects, oronasal fistulae, periodontal bone loss, and angular limb-deformity correction.',
    },
    {
      q: 'Is FlexiOss® Vet the same technology as the human product?',
      a: 'Yes. FlexiOss® Vet uses the same human-grade, third-generation hydroxyapatite–curdlan regenerative science, formulated for veterinary surgery.',
    },
    {
      q: 'Is there peer-reviewed evidence for FlexiOss® Vet?',
      a: 'Yes — including Misztal-Kunecka et al. (Life 2024;14:879) on bone defects associated with ectopic tooth extraction in dogs, plus related alveolar-regeneration studies in advanced periodontal disease.',
    },
    {
      q: 'Does FlexiOss® Vet require a removal reoperation?',
      a: 'No. It is osteoconductive and resorbable — it integrates and is gradually remodelled into natural bone, so no removal procedure is needed.',
    },
  ],
});
